// src/components/StoreSettingsWizard/index.jsx
import React, { useEffect, useState, useCallback } from "react";
import { useForm, FormProvider } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import schema from "./validationSchema";
import { mergeDeep } from "./utils/mergeDeep";
import { getSettings, saveSettings } from "./utils/api";
import StorePoliciesTab from "./StorePoliciesTab";
import ShippingTab from "./ShippingTab";
import PaymentsTab from "./PaymentsTab";
import Tabs from "./common/Tabs";
import { useSearchParams } from 'react-router-dom';
    

/**
 * Wizard scaffold – provides:
 *  • central form context (react-hook-form)
 *  • dirty / loading / saving state handling
 *  • tab navigation
 */
export default function StoreSettingsWizard() {
  const methods = useForm({
    defaultValues: {},
    resolver: yupResolver(schema),
    mode: "onChange",
  });
  const [searchParams] = useSearchParams();
  const businessId = searchParams.get('business_id');
console.log(businessId);
  const {
    formState,
    reset,
    handleSubmit,
    trigger,          //  ← NEW
  } = methods;
  const { isDirty, isValid, errors }       = formState;

  /* --------- lifecycle: fetch on mount ------------------------------ */
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving,  setIsSaving]  = useState(false);
  const [error,     setError]     = useState(null);
  console.log(errors);
  useEffect(() => {
    let aborted = false;
    (async () => {
      try {
        if (!businessId) throw new Error("Missing businessId in URL");
        const data = await getSettings(businessId);
        if (!aborted) {
          const merged = mergeDeep(DEFAULT_SETTINGS, data || {});
          reset(merged, { keepDirty: false });
          await trigger();            //  ← run first validation pass
        }
      } catch {
        if (!aborted) {
          reset(DEFAULT_SETTINGS, { keepDirty: false });
          await trigger();            //  ← still validate
        }
      } finally {
        if (!aborted) setIsLoading(false);
      }
    })();
    return () => { aborted = true; };
  }, [reset, trigger, businessId]);
  /* --------- submit (PUT) ------------------------------------------- */
  const onSubmit = useCallback(
    async (payload) => {
      setIsSaving(true);
      setError(null);
      try {
        await saveSettings(businessId, payload);
        reset(payload, { keepDirty: false });
      } catch (err) {
        setError(err.message || "Unknown error");
      } finally {
        setIsSaving(false);
      }
    },
    [reset, businessId]
  );

  if (isLoading)
    return (
      <div className="flex h-40 items-center justify-center">
        <span className="animate-pulse text-gray-500">Loading settings…</span>
      </div>
    );

  return (
    <FormProvider {...methods}>
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="mx-auto max-w-5xl space-y-8"
      >
        {/* Header */}
        <header className="flex items-center justify-between">
          <h1 className="text-2xl font-semibold">Store Settings Wizard</h1>
          {isSaving && (
            <span className="animate-pulse text-sm text-gray-500">Saving…</span>
          )}
        </header>

        {/* Tabs */}
        <Tabs
          tabs={[
            { label: "Store Policies",  component: <StorePoliciesTab /> },
            { label: "Shipping & Delivery", component: <ShippingTab /> },
            { label: "Payments",        component: <PaymentsTab /> },
          ]}
        />

        {/* Global form errors */}
        {Object.keys(errors).length > 0 && (
          <p className="rounded bg-red-50 p-3 text-sm text-red-700">
            Please fix the highlighted fields.
          </p>
        )}
        {error && (
          <p className="rounded bg-red-50 p-3 text-sm text-red-700">
            {error}. Try again.
          </p>
        )}

        {/* Footer actions */}
        <button
          type="submit"
          disabled={!isDirty || !isValid || isSaving}
          className={`rounded-lg px-6 py-2 text-white shadow
            ${isDirty && isValid ? "bg-indigo-600 hover:bg-indigo-700"
                                 : "bg-gray-400"}
            disabled:opacity-50`}
        >
          {isSaving ? "Saving…" : isDirty ? "Save" : "Saved"}
        </button>
      </form>
    </FormProvider>
  );
}

/* -------------- fallback defaults (filled forward-compat) ----------- */
const DEFAULT_SETTINGS = {
  policies: { returnPolicy: "", termsOfSale: "" },
  shippingGroups: [],
  payments: {
    currency: "USD",
    notificationEmail: "",
    contactEmail: "",
    contactPhone: "",
  },
};
